import { Inject, Injectable } from '@angular/core';
import { ImageModel, MenuItemKey, NavigationArea, NavigationMenuItem, SettingsModel } from '../models';
import { SETTINGS_SERVICE } from '../constants';
import { BaseSettingsGetService } from './base-settings.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationMenuService {
  private menuItems: NavigationMenuItem[] = [];

private _topSidebarMenuKeysCustom: MenuItemKey[] = [];
  private readonly _topSidebarMenuKeys = [
    MenuItemKey.LEARNING_PATH,
    MenuItemKey.CATALOG,
    MenuItemKey.EVENTS,
    MenuItemKey.CALENDAR,
    MenuItemKey.LIBRARY,
    MenuItemKey.COMMUNITY,
    MenuItemKey.SITEMAP,
    MenuItemKey.ACHIEVEMENTS,
    MenuItemKey.LEADERBOARD
  ];
  public get topSidebarMenuKeys(): MenuItemKey[] {
    return [...this._topSidebarMenuKeys, ...this._topSidebarMenuKeysCustom];
  }

  private _bottomSidebarMenuKeysCustom: MenuItemKey[] = [];
  private readonly _bottomSidebarMenuKeys = [
    MenuItemKey.SEARCH,
    MenuItemKey.CHAT,
    MenuItemKey.AI_CHAT,
    MenuItemKey.NOTIFICATIONS,
    MenuItemKey.LANGUAGES,
    MenuItemKey.ADMIN_PANEL,
    MenuItemKey.MANAGER_VIEW,
    MenuItemKey.HELP,
    MenuItemKey.LEGAL_CENTER,
    MenuItemKey.SWITCH_BRANCH,
    MenuItemKey.LOG_OUT
  ];
  public get bottomSidebarMenuKeys(): MenuItemKey[] {
    return [...this._bottomSidebarMenuKeys, ...this._bottomSidebarMenuKeysCustom];
  }

  private _rightMenuKeysCustom: MenuItemKey[] = [];
  private readonly _rightMenuKeys = [
    MenuItemKey.SEARCH,
    MenuItemKey.CHAT,
    MenuItemKey.NOTIFICATIONS,
    MenuItemKey.LANGUAGES,
    MenuItemKey.ADMIN_PANEL,
    MenuItemKey.AI_CHAT
  ];
  public get rightMenuKeys(): MenuItemKey[] {
    return [...this._rightMenuKeys, ...this._rightMenuKeysCustom];
  }

  private _bottomNativeBarMenuKeysCustom: MenuItemKey[] = [];
  private readonly _bottomNativeBarMenuKeys = [
    MenuItemKey.HOME,
    MenuItemKey.LEARNING_PATH,
    MenuItemKey.CATALOG,
    MenuItemKey.EVENTS,
    MenuItemKey.CALENDAR,
    MenuItemKey.LIBRARY,
    MenuItemKey.COMMUNITY,
    MenuItemKey.SITEMAP,
    MenuItemKey.ACHIEVEMENTS,
    MenuItemKey.LEADERBOARD,
    MenuItemKey.CHAT,
    MenuItemKey.SEARCH,
    MenuItemKey.PROFILE,
    MenuItemKey.AI_CHAT
  ];
  public get bottomNativeBarMenuKeys(): MenuItemKey[] {
    return [...this._bottomNativeBarMenuKeys, ...this._bottomNativeBarMenuKeysCustom];
  }

  constructor(
    @Inject(SETTINGS_SERVICE) private readonly settingsService: BaseSettingsGetService,
  ) {
    settingsService.getSettings().subscribe(data => {
      this.initialize(data);
    });
  }

  public initialize(settings: SettingsModel): void {
    this.setCustomMenuKeys(settings.menu);
    this.setMenuItems(settings);
  }

  private setMenuItems(settings: SettingsModel): void {
    this.menuItems = [
      {
        title: 'COMMON.home',
        path: '/dashboard',
        icon: 'house',
        key: MenuItemKey.HOME
      },
      {
        title: 'COMMON.profile',
        path: '/profile',
        icon: 'user',
        key: MenuItemKey.PROFILE
      },
      {
        title: 'LEARNING_PATH.learning_paths',
        path: '/learning-paths',
        icon: 'path',
        key: MenuItemKey.LEARNING_PATH
      },
      {
        title: 'MENU.courses',
        path: '/classes',
        icon: 'book-open-text',
        availableOffline: true,
        key: MenuItemKey.CATALOG
      },
      {
        title: 'MENU.events',
        path: '/webinars',
        icon: 'monitor-play',
        key: MenuItemKey.EVENTS
      },
      {
        title: 'MENU.calendar',
        path: '/calendar',
        icon: 'calendar',
        key: MenuItemKey.CALENDAR
      },
      {
        title: 'MENU.library',
        path: '/library',
        icon: 'books',
        key: MenuItemKey.LIBRARY
      },
      {
        title: 'MENU.community',
        path: '/forum',
        icon: 'chats',
        key: MenuItemKey.COMMUNITY
      },
      {
        title: 'MENU.sitemap',
        path: '/sitemap',
        icon: 'tree-structure',
        key: MenuItemKey.SITEMAP
      },
      {
        title: 'ACHIEVEMENTS.my_achievements',
        path: '/my-achievements',
        icon: 'graduation-cap',
        key: MenuItemKey.ACHIEVEMENTS
      },
      {
        title: 'LEADERBOARD.leaderboard',
        path: '/leaderboard',
        icon: 'trophy',
        key: MenuItemKey.LEADERBOARD
      },
      {
        title: 'MANAGER_VIEW.title',
        path: '/manager-view',
        icon: 'trend-up',
        key: MenuItemKey.MANAGER_VIEW
      },
      {
        title: 'COMMON.help',
        icon: 'lifebuoy',
        key: MenuItemKey.HELP
      },
      {
        title: 'COMMON.legal_center',
        icon: 'scales',
        path: '/profile/legal-center',
        key: MenuItemKey.LEGAL_CENTER
      },
      {
        title: 'BRANCH.switch_branch',
        icon: 'repeat',
        path: '/branches',
        key: MenuItemKey.SWITCH_BRANCH
      },
      {
        title: 'MENU.search',
        icon: 'magnifying-glass',
        key: MenuItemKey.SEARCH
      },
      {
        title: 'COMMON.chat',
        icon: 'chat-dots',
        key: MenuItemKey.CHAT
      },
      {
        title: 'COMMON.languages',
        icon: 'globe',
        key: MenuItemKey.LANGUAGES
      },
      {
        title: 'COMMON.admin_area',
        icon: 'pencil-simple',
        key: MenuItemKey.ADMIN_PANEL
      },
      {
        title: 'MENU.logout',
        icon: 'sign-out',
        key: MenuItemKey.LOG_OUT
      },
      {
        title: 'COMMON.notifications',
        icon: 'bell',
        key: MenuItemKey.NOTIFICATIONS
      },
      {
        title: settings.aiChatbot?.name || 'COMMON.ai_chat',
        path: '/ai-chat',
        key: MenuItemKey.AI_CHAT,
        ...this.getAiChatIcon(settings)
      },
      ...this.getCustomMenuItems(settings.menu)
    ];
  }

  public getTopSidebarMenuItems(): NavigationMenuItem[] {
    return this.menuItems.filter(item => this.topSidebarMenuKeys.indexOf(item.key) !== -1);
  }

  public getBottomSidebarMenuItems(): NavigationMenuItem[] {
    return this.menuItems.filter(item => this.bottomSidebarMenuKeys.indexOf(item.key) !== -1);
  }

  public getRightMenuItems(): NavigationMenuItem[] {
    return this.menuItems.filter(item => this.rightMenuKeys.indexOf(item.key) !== -1);
  }

  public getBottomNativeBarMenuItems(): NavigationMenuItem[] {
    return this.menuItems.filter(item => this.bottomNativeBarMenuKeys.indexOf(item.key) !== -1);
  }

  public sortMenuPreserveUnweighted(items: NavigationMenuItem[]): void {
    const weightedItems = items
      .filter(item => Number.isFinite(item.weight))
      .sort((a, b) => (a.weight as number) - (b.weight as number));

    let weightedIndex = 0;

    for (let i = 0; i < items.length; i++) {
      if (Number.isFinite(items[i].weight)) {
        items[i] = weightedItems[weightedIndex++];
      }
    }
  }

  public setCustomMenuKeys(menuSettings: any): void {
    const getCustomKeys: (menuGroupKey: NavigationArea) => MenuItemKey[]  = (menuGroupKey: NavigationArea) => Object.keys(menuSettings[menuGroupKey]).filter(menuItemKey => menuSettings[menuGroupKey]?.[menuItemKey]?.custom) as MenuItemKey[];
    this._topSidebarMenuKeysCustom = getCustomKeys(NavigationArea.TOP_SIDEBAR);
    this._bottomSidebarMenuKeysCustom = getCustomKeys(NavigationArea.BOTTOM_SIDEBAR);
    this._bottomNativeBarMenuKeysCustom = getCustomKeys(NavigationArea.BOTTOM_NATIVE_BAR);
    this._rightMenuKeysCustom = getCustomKeys(NavigationArea.RIGHT_MENU);
  }

  private getAiChatIcon(settings: SettingsModel): { icon?: string, iconMedia?: ImageModel } {
    if (settings.aiChatbot?.menuIcon) {
      return { iconMedia: settings.aiChatbot?.menuIcon }
    }

    return { icon: 'sparkle' };
  }

  private getCustomMenuItems(menuSettings: any): NavigationMenuItem[] {
    return Object.values(NavigationArea)
      .reduce((acc, menuKey) => {
        const customMenuList = Object.keys(menuSettings?.[menuKey])
          .filter(groupKey => menuSettings[menuKey]?.[groupKey]?.custom)
          .map(groupKey => ({...menuSettings[menuKey]?.[groupKey], key: groupKey}));
        acc = [...acc, ...customMenuList];
        return acc;
      }, [])
  }

}
